import { PADDING, getNodeDimensions } from '../constants';
import { expandEdgesThroughMilestones } from '../../utils/edgePaths/edgeGeneration';
import { buildEdgePathMapFromElkGraph } from '../elkEdgePathAdapter';
import { getMilestoneAnchor, getMilestoneLayoutDims } from '../../utils/milestoneUtils';
import { getNoteAnchor, getNoteLayoutDims, isNoteNode } from '../../utils/noteUtils';
import { buildRootLayoutOptions } from './elkOptions';

/**
 * Collect node positions from ELK layout result (direct children of the laid-out root).
 */
function collectElkNodePositions(layouted) {
  const positions = {};
  for (const node of layouted.children || []) {
    if (node?.id && node.id !== 'elk-root') {
      positions[node.id] = { x: node.x ?? 0, y: node.y ?? 0 };
    }
  }
  return positions;
}

/**
 * Milestones on a host with no logical edge path still need an ELK edge to the host
 * so layered layout places them adjacent to the mission (not as isolated roots at y=0).
 */
function ensureMilestoneHostAnchorEdges(edges, componentTasks, taskIdSet) {
  const connectedMilestoneIds = new Set();
  edges.forEach((edge) => {
    edge.sources.forEach((id) => connectedMilestoneIds.add(id));
    edge.targets.forEach((id) => connectedMilestoneIds.add(id));
  });

  componentTasks.forEach((task) => {
    if (task.nodeType !== 'milestone') return;
    if (connectedMilestoneIds.has(task.id)) return;

    const anchor = getMilestoneAnchor(task);
    if (!anchor?.hostTaskId || !taskIdSet.has(anchor.hostTaskId)) return;

    if (anchor.side === 'input') {
      edges.push({
        id: `${task.id}-${anchor.hostTaskId}-ms-anchor`,
        sources: [task.id],
        targets: [anchor.hostTaskId],
      });
    } else {
      edges.push({
        id: `${anchor.hostTaskId}-${task.id}-ms-anchor`,
        sources: [anchor.hostTaskId],
        targets: [task.id],
      });
    }
  });
}

function ensureNoteHostAnchorEdges(edges, componentTasks, taskIdSet) {
  const connectedNoteIds = new Set();
  edges.forEach((edge) => {
    edge.sources.forEach((id) => connectedNoteIds.add(id));
    edge.targets.forEach((id) => connectedNoteIds.add(id));
  });

  componentTasks.forEach((task) => {
    if (!isNoteNode(task)) return;
    if (connectedNoteIds.has(task.id)) return;

    const hostTaskId = getNoteAnchor(task)?.hostTaskId;
    if (!hostTaskId || !taskIdSet.has(hostTaskId)) return;

    edges.push({
      id: `${hostTaskId}-${task.id}-note-anchor`,
      sources: [hostTaskId],
      targets: [task.id],
    });
  });
}

export async function layoutElkComponent(elk, componentTasks, nodeDimensions) {
  if (!componentTasks.length) {
    return { positions: {}, edgePaths: {}, links: [] };
  }

  const taskIdSet = new Set(componentTasks.map((task) => task.id));

  const children = componentTasks.map((task) => {
    let dims;
    if (task.nodeType === 'milestone') {
      dims = getMilestoneLayoutDims(nodeDimensions, task.id);
    } else if (isNoteNode(task)) {
      dims = getNoteLayoutDims(nodeDimensions, task.id);
    } else {
      dims = getNodeDimensions(task.id, nodeDimensions);
    }
    return {
      id: task.id,
      width: dims.width,
      height: dims.height,
    };
  });

  const edges = [];
  expandEdgesThroughMilestones(componentTasks).forEach(({ from: depId, to: targetId, branch }) => {
    if (!depId || !targetId || !taskIdSet.has(depId) || !taskIdSet.has(targetId)) return;
    const edgeId = `${depId}-${targetId}${branch ? `-${branch}` : ''}`;
    edges.push({
      id: edgeId,
      sources: [depId],
      targets: [targetId],
    });
  });

  ensureMilestoneHostAnchorEdges(edges, componentTasks, taskIdSet);
  ensureNoteHostAnchorEdges(edges, componentTasks, taskIdSet);

  const graph = {
    id: 'elk-root',
    layoutOptions: buildRootLayoutOptions(componentTasks.length, edges.length),
    children,
    edges,
  };

  const layouted = await elk.layout(graph);
  const positions = collectElkNodePositions(layouted);

  componentTasks.forEach((task) => {
    if (!positions[task.id]) {
      console.warn('[calculateLayoutElk] Missing ELK position for task:', task.id);
      positions[task.id] = { x: PADDING, y: PADDING };
    }
  });

  const edgePaths = buildEdgePathMapFromElkGraph(layouted);
  const links = edges
    .map((edge) => ({
      id: edge.id,
      source: edge.sources?.[0],
      target: edge.targets?.[0],
    }))
    .filter((edge) => edge.id && edge.source && edge.target);
  return { positions, edgePaths, links };
}
